import React, { useEffect, useRef, useState } from "react"
import intl from "react-intl-universal"
import { BASE_API_URL } from "constants/General"
import { useAppState } from "graphql/hooks/useAppState"
import { track } from "utils/track"
import { AiCreditsState, getAiCreditsState } from "../aiCredits"
import { MagicCreditsInfo } from "../types"

/**
 * Explains why a creator cannot spend AI Tasks, and lets them ask an admin.
 *
 * Workspaces that meter AI credits per creator start everyone at **zero**.
 * Without an explanation "0 Available" reads as a bug or a downgrade, and every
 * one of those creators becomes a support ticket — so this is what makes the
 * feature releasable, not a nicety.
 *
 * Renders nothing unless the workspace meters per creator AND the viewer is
 * short, so it stays invisible everywhere else.
 */


type Props = {
  credits?: MagicCreditsInfo | null
  organizationId?: string
  /**
   * Billing admins are told to assign credits themselves. Sending them a request
   * button would have them email their own request.
   */
  canAssignAiTasks?: boolean
  /** True once AI Tasks have been requested by any route this session. */
  alreadyRequested?: boolean
  /** Fired once a request has actually reached an admin. */
  onAiCreditsRequested?: () => void
  compact?: boolean
}

const AiCreditsNotice = ({ credits, organizationId, canAssignAiTasks = false, alreadyRequested = false, onAiCreditsRequested, compact = false }: Props) => {
  const appState = useAppState()
  const [pending, setPending] = useState(false)
  const [outcome, setOutcome] = useState<'idle' | 'sent' | 'nobody' | 'rateLimited'>('idle')
  const [notifiedRole, setNotifiedRole] = useState<'billingAdmin' | 'admin' | undefined>()

  /**
   * There is deliberately no timer putting the link back.
   *
   * One request per session is the intent: the server allows one per hour anyway,
   * and a link that reappeared while the modal already said "asked" read as two
   * components disagreeing. The reason for the block stays on screen throughout.
   */

  const state: AiCreditsState = getAiCreditsState(credits)
  if (state === 'ok') return null

  const notAssigned = state === 'notAssigned'
  const poolEmpty = state === 'workspaceExhausted'
  const lowOnly = state === 'low'
  const remaining = credits?.creator?.availableCredits ?? 0

  const message = poolEmpty
    // Nobody can assign their way out of an empty pool, so this message never
    // points at the roster — not even for an admin.
    ? (canAssignAiTasks
      ? intl.get('ai_credits_workspace_exhausted_note_admin')
      : intl.get('ai_credits_workspace_exhausted_note'))
    : lowOnly
      // Said in words as well as in amber: colour alone is invisible to a screen
      // reader, and to anyone who cannot distinguish it.
      ? (canAssignAiTasks
        ? intl.get('ai_credits_creator_low_note_admin', {0: remaining})
        : intl.get('ai_credits_creator_low_note', {0: remaining}))
      : canAssignAiTasks
        ? notAssigned
          ? intl.get('ai_credits_creator_not_assigned_note_admin')
          : intl.get('ai_credits_creator_depleted_note_admin')
        : notAssigned
          ? intl.get('ai_credits_creator_not_assigned_note')
          : intl.get('ai_credits_creator_depleted_note')

  const requestCredits = async () => {
    if (!organizationId || !appState || pending) return
    setPending(true)
    try {
      const response = await fetch(
        `${BASE_API_URL}/api/v2/organizations/${organizationId}/ai-credits/request`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${appState.loggedInAs.token}`,
          },
          // Send the shortfall, not just "more": the admin's email has a slot for
          // it, and "Bojan needs 120" is actionable where "Bojan wants some" is a
          // reply-and-wait. Omitted before the first estimate lands.
          body: JSON.stringify(credits?.totalCredits ? {requestedCredits: credits.totalCredits} : {}),
        },
      )

      // 429 is the endpoint's one-per-hour limit, not a failure: the earlier
      // request was delivered, so a repeat press must read as "already sent".
      if (response.status === 429) {
        // Nothing was sent just now — an earlier request was. Saying "sent" here
        // claimed an email had gone out when none had, which made a genuinely
        // failed first request impossible to spot.
        setOutcome('rateLimited')
        onAiCreditsRequested?.()
        return
      }
      if (!response.ok) {
        setOutcome('idle')
        return
      }

      const body = await response.json()
      const data = body?.data || body
      setNotifiedRole(data?.role)
      setOutcome(data?.sent ? 'sent' : 'nobody')
      if (data?.sent) onAiCreditsRequested?.()

      track({
        event: 'AI Creator Credits - Request Sent From Modal',
        variables: {
          organizationId,
          recipients: data?.recipients ?? 0,
          isAssigned: !!credits?.creator?.isAssigned,
          platform: 'web',
        },
      })
    } catch {
      setOutcome('idle')
    } finally {
      setPending(false)
    }
  }

  const confirmation = outcome === 'rateLimited'
    ? intl.get('ai_credits_request_already_sent')
    : outcome === 'nobody'
      ? intl.get('ai_credits_request_sent_nobody')
      : notifiedRole === 'admin'
        ? intl.get('ai_credits_request_sent_admin')
        : intl.get('ai_credits_request_sent')

  return (
    <div data-test="ai-credits-notice" className={compact ? 'text-xs max-w-xs' : 'text-xs mt-2'}>
      <div className="text-gray-600">{message}</div>

      {outcome === 'idle' && !alreadyRequested && !canAssignAiTasks && !poolEmpty && !!organizationId && (
        <button
          type="button"
          data-test="ai-credits-request-button"
          onClick={requestCredits}
          disabled={pending}
          className={`mt-1 font-bold underline text-magic ${pending ? 'opacity-50' : ''}`}
        >
          {pending ? intl.get('global_sending') : intl.get('ai_credits_request_from_admin')}
        </button>
      )}

      {outcome !== 'idle' && (
        <div
          data-test="ai-credits-request-confirmation"
          className={`mt-1 ${outcome === 'nobody' ? 'text-coral' : 'text-gray-600'}`}
        >
          {confirmation}
        </div>
      )}
    </div>
  )
}

export default AiCreditsNotice
